import { useEffect, useState } from "react";
import { api } from "../lib/api.js";
import { SeatManager } from "./SeatManager.js";

interface CustomerLicence {
  id: string;
  licenceId: string;
  licensedTo: string;
  seatsPurchased: number;
  startDate: string;
  expiryDate: string;
  status: "active" | "suspended" | "revoked" | "lapsed" | string;
}

interface CustomerOverview {
  customerName: string;
  licences: CustomerLicence[];
}

/**
 * "My Account" - the customer self-service portal. Rendered only once
 * AuthGate has confirmed (via GET /api/me) the signed-in user has the
 * "customer" role. Deliberately has no link/route into any admin view -
 * everything it shows comes from /api/customer/* Functions, which scope
 * every query to the caller's own customer_id server-side.
 */
export function CustomerPortal() {
  const [overview, setOverview] = useState<CustomerOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [renewedBanner, setRenewedBanner] = useState<string | null>(null);

  // Returning from Stripe Checkout lands on /?renewed=1 (or /?renew_cancelled=1).
  // Show a banner, then strip the query param so a refresh doesn't repeat it.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has("renewed")) {
      setRenewedBanner("Payment received - your licence has been renewed. It may take a moment to update below.");
    } else if (params.has("renew_cancelled")) {
      setRenewedBanner("Renewal cancelled - no payment was taken.");
    }
    if (params.has("renewed") || params.has("renew_cancelled")) {
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  async function load() {
    try {
      const res = await api.get<CustomerOverview>("/customer/overview");
      setOverview(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load your account");
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function handleDownload() {
    setDownloading(true);
    setDownloadError(null);
    try {
      const res = await api.get<{ downloadUrl: string }>("/customer/download");
      window.location.href = res.downloadUrl;
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : "Failed to get download link");
    } finally {
      setDownloading(false);
    }
  }

  const [erasureState, setErasureState] = useState<"idle" | "sending" | "sent">("idle");
  async function requestErasure() {
    if (
      !window.confirm(
        "Request deletion of your AudioSwytch account and data? We'll confirm by email and action it within 30 days (after any legal/accounting retention). Your licences will stop working."
      )
    )
      return;
    setErasureState("sending");
    try {
      await api.post("/customer/erasure-request");
      setErasureState("sent");
    } catch {
      setErasureState("idle");
      window.alert("Sorry, we couldn't submit that. Please email support@audioswytch.co.uk.");
    }
  }

  if (error) return <p className="error-text">{error}</p>;
  if (!overview) return <p>Loading…</p>;

  return (
    <div className="app-content">
      <h1>My Account</h1>
      <p className="muted-subtitle">{overview.customerName}</p>

      {renewedBanner && <div className="warning-box" style={{ marginBottom: 12 }}>{renewedBanner}</div>}

      <div className="button-row">
        <button className="primary" onClick={handleDownload} disabled={downloading}>
          {downloading ? "Preparing download…" : "Download AudioSwytch"}
        </button>
      </div>
      {downloadError && <p className="error-text">{downloadError}</p>}

      <h2>Your licence{overview.licences.length === 1 ? "" : "s"}</h2>
      {overview.licences.length === 0 && (
        <p>No licences on file yet - contact support@audioswytch.co.uk.</p>
      )}
      {overview.licences.map((l) => {
        return (
          <div key={l.id} className="card">
            <div className="row-between">
              <strong>{l.licensedTo}</strong>
              <span className={`status-badge status-${l.status}`}>{l.status}</span>
            </div>
            <p>
              Licensed for {l.seatsPurchased} device{l.seatsPurchased === 1 ? "" : "s"}
            </p>
            <p>
              Valid {l.startDate} → {l.expiryDate}
            </p>
            <SeatManager licenceId={l.id} />
          </div>
        );
      })}

      <h2 style={{ marginTop: 28 }}>Privacy &amp; data</h2>
      {erasureState === "sent" ? (
        <p className="warning-box">
          Your deletion request has been received. We'll confirm by email and action it within 30 days.
        </p>
      ) : (
        <p style={{ color: "var(--muted)", fontSize: 14 }}>
          You can request deletion of your account and personal data at any time. We'll confirm by email and action it
          within 30 days (after any legal or accounting retention).{" "}
          <button className="link-btn" onClick={requestErasure} disabled={erasureState === "sending"}>
            {erasureState === "sending" ? "Sending…" : "Request data deletion"}
          </button>
        </p>
      )}
    </div>
  );
}
